// src/component/add_product.js
import axios from "axios";
import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  MenuItem,
  IconButton,
  CircularProgress,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveIcon from "@mui/icons-material/Remove";
import { ImagePreviewDialog } from "./ImagePreview";
import { ImageUploadField } from "./ImageUploadField";

import { getAuthHeaders, useAuth } from "../../context/AuthContext";
import { REACT_APP_API_BASE_URL } from "../../config";
import { metadataOptions } from "../../enums/metadataOptions";

const defaultMetaFields = [
  { key: "status", value: "active", error: false }, // mandatory
  { key: "gender", value: "", error: false },
  { key: "price", value: "", error: false },
];

export default function AddProduct() {
  const [sku, setSku] = useState("");
  const [mainImage, setMainImage] = useState(null);
  const [otherImages, setOtherImages] = useState([]);
  const [response, setResponse] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [metaFields, setMetaFields] = useState(defaultMetaFields);
  const { user } = useAuth();

  // Returns metadata options that aren’t already selected
  const getAvailableKeys = () => {
    const usedKeys = metaFields.map((m) => m.key);
    return metadataOptions.filter((opt) => !usedKeys.includes(opt.key));
  };

  const handleMetaFieldChange = (idx, field, value) => {
    setMetaFields((prev) => {
      const updated = [...prev];
      if (field === "key") {
        updated[idx].key = value;
        updated[idx].value = ""; // reset value on key change
        updated[idx].error = false;
      } else if (field === "value") {
        const metaType = metadataOptions.find(
          (opt) => opt.key === updated[idx].key
        )?.type;
        if (metaType === "numeric") {
          updated[idx].value = value;
          updated[idx].error = updated[idx].value === "";
        } else {
          updated[idx].value = value;
          updated[idx].error = updated[idx].value === "";
        }
      }
      return updated;
    });
  };

  const handleAddMetaField = () => {
    const available = getAvailableKeys();
    if (available.length > 0) {
      setMetaFields((prev) => [
        ...prev,
        { key: available[0].key, value: "", error: false },
      ]);
    }
  };

  const handleRemoveMetaField = (idx) => {
    // Prevent deletion of mandatory "status" field
    if (metaFields[idx].key === "status") return;
    setMetaFields((prev) => prev.filter((_, i) => i !== idx));
  };

  const handlePreview = (imgFile) => {
    if (!imgFile) return;
    const url = URL.createObjectURL(imgFile);
    setPreviewImage(url);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    if (previewImage) URL.revokeObjectURL(previewImage);
    setPreviewOpen(false);
    setPreviewImage(null);
  };

  /* ------------------------------ submit --------------------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic field validation
    if (!sku.trim()) {
      setResponse("SKU is required");
      return;
    }
    if (!mainImage) {
      setResponse("Main image is required");
      return;
    }

    // Validate metadata
    for (const m of metaFields) {
      const metaType = metadataOptions.find((opt) => opt.key === m.key)?.type;
      if (
        !m.key ||
        m.value === undefined ||
        m.value === null ||
        m.value === "" ||
        (metaType === "boolean" && m.value !== "yes" && m.value !== "no")
      ) {
        setResponse("All metadata fields must be filled");
        return;
      }
    }

    setResponse("Uploading...");

    try {
      const headers = await getAuthHeaders();
      const totalImages = 1 + otherImages.length;
      const res = await axios.get(
        `${REACT_APP_API_BASE_URL}/get-multiple-upload-urls/product?num_urls=${totalImages}`,
        { headers }
      );
      const { urls } = res.data;

      const mainImageInfo = urls[0];
      const mainUploadUrl = mainImageInfo.upload_url;

      //Uploading all images at asynchronously
      const mainImagePromise = axios.put(mainUploadUrl, mainImage, {
        headers: { "Content-Type": mainImage.type },
      });
      const otherImagePromises = otherImages.map((image, i) => {
        const imageInfo = urls[i + 1];
        return axios.put(imageInfo.upload_url, image, {
          headers: { "Content-Type": image.type },
        });
      });
      await Promise.all([mainImagePromise, ...otherImagePromises]);
      const otherUploadUrls = urls.slice(1).map((info) => info.upload_url);

      // Payload
      const metadata = {};
      metaFields.forEach(({ key, value }) => {
        const metaType = metadataOptions.find((opt) => opt.key === key)?.type;
        if (metaType === "numeric") {
          metadata[key] = parseFloat(value);
        } else if (metaType === "boolean") {
          metadata[key] = value === "yes";
        } else {
          metadata[key] = value;
        }
      });
      // Add creation time and user email to metadata
      const details = {};
      details.created_at = new Date() * 1000000;
      details.created_by = user?.email || "unknown";
      details.source = "add_product";

      const payload = {
        sku,
        main_image_url: mainUploadUrl,
        other_images_url: otherUploadUrls,
        metadata,
        details,
      };
      try {
        const res = await axios.post(
          `${REACT_APP_API_BASE_URL}/products/`,
          payload,
          { headers }
        );
        if (res.status === 200) {
          setResponse("Product added successfully!");
          setSku("");
          setMetaFields([
            { key: "status", value: "active", error: false },
            { key: "gender", value: "", error: false },
            { key: "price", value: "", error: false },
          ]);
          setMainImage(null);
          setOtherImages([]);
        }
      } catch (apiError) {
        const errorMessage = apiError.response.data.detail;
        setResponse(`Failed to add product: ${errorMessage}`);
      }
    } catch (err) {
      console.error(err);
      setResponse("Upload failed: " + err.message);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 600, mx: "auto" }}>
      <Typography
        variant="h5"
        gutterBottom
        sx={{ flexGrow: 1, fontWeight: "bold" }}
      >
        Add Product
      </Typography>

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <TextField
          label="SKU"
          value={sku}
          onChange={(e) => setSku(e.target.value)}
          fullWidth
          margin="normal"
        />

        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", mt: 1, mb: 1 }}>
            <Typography variant="subtitle1" sx={{ mr: 1 }}>
              Metadata
            </Typography>
            <IconButton color="primary" onClick={handleAddMetaField}>
              <AddCircleOutlineIcon />
            </IconButton>
          </Box>

          {metaFields.map((m, idx) => {
            const metaType = metadataOptions.find(
              (opt) => opt.key === m.key
            )?.type;

            return (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  mb: 1,
                  gap: 1,
                }}
              >
                <TextField
                  label="Key"
                  select
                  value={m.key}
                  onChange={(e) =>
                    handleMetaFieldChange(idx, "key", e.target.value)
                  }
                  sx={{
                    minWidth: { xs: 100, sm: 180 },
                    maxWidth: { xs: "100%", sm: 220 },
                    flex: 1,
                  }}
                  disabled={m.key === "status"}
                  InputLabelProps={{ shrink: true }}
                >
                  {metadataOptions.map((opt) => (
                    <MenuItem
                      key={opt.key}
                      value={opt.key}
                      disabled={
                        metaFields.some(
                          (f, i) => f.key === opt.key && i !== idx
                        ) ||
                        (opt.key === "status" && m.key !== "status")
                      }
                    >
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>

                {metaType === "boolean" ? (
                  <TextField
                    label="Value"
                    select
                    value={m.value}
                    onChange={(e) =>
                      handleMetaFieldChange(idx, "value", e.target.value)
                    }
                    sx={{
                      minWidth: { xs: 100, sm: 180 },
                      maxWidth: { xs: "100%", sm: 220 },
                      flex: 1,
                    }}
                    InputLabelProps={{ shrink: true }}
                  >
                    <MenuItem value="">
                      <em>Select...</em>
                    </MenuItem>
                    <MenuItem value="yes">Yes</MenuItem>
                    <MenuItem value="no">No</MenuItem>
                  </TextField>
                ) : metaType === "status" ? (
                  <TextField
                    label="Value"
                    select
                    value={m.value}
                    onChange={(e) =>
                      handleMetaFieldChange(idx, "value", e.target.value)
                    }
                    sx={{
                      minWidth: { xs: 100, sm: 180 },
                      maxWidth: { xs: "100%", sm: 220 },
                      flex: 1,
                    }}
                    InputLabelProps={{ shrink: true }}
                  >
                    <MenuItem value="archived">archived</MenuItem>
                    <MenuItem value="active">active</MenuItem>
                    <MenuItem value="draft">draft</MenuItem>
                  </TextField>
                ) : (
                  <TextField
                    label="Value"
                    value={m.value}
                    onChange={(e) => {
                      if (metaType === "numeric") {
                        // Allow only digits and a single decimal point
                        let val = e.target.value.replace(/[^0-9.]/g, "");
                        const parts = val.split(".");
                        if (parts.length > 2) {
                          val = parts[0] + "." + parts.slice(1).join("");
                        }
                        handleMetaFieldChange(idx, "value", val);
                      } else {
                        handleMetaFieldChange(idx, "value", e.target.value);
                      }
                    }}
                    error={!!m.error}
                    sx={{
                      minWidth: { xs: 100, sm: 180 },
                      maxWidth: { xs: "100%", sm: 220 },
                      flex: 1,
                    }}
                    type={metaType === "numeric" ? "number" : "text"}
                    inputProps={
                      metaType === "numeric"
                        ? { step: "any", min: 0, pattern: "[0-9.]*" }
                        : {}
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                )}

                {/* delete icon (except STATUS) */}
                {m.key !== "status" && (
                  <IconButton
                    color="primary"
                    onClick={() => handleRemoveMetaField(idx)}
                  >
                    <RemoveIcon />
                  </IconButton>
                )}
              </Box>
            );
          })}
        </Box>
        {/* Main image */}
        <ImageUploadField
          label="Main Image"
          images={mainImage}
          onChange={setMainImage}
          onRemove={() => setMainImage(null)}
          onPreview={handlePreview}
        />
        {/* Other images */}
        <ImageUploadField
          label="Other Images"
          multiple
          images={otherImages}
          onChange={(files) => setOtherImages((prev) => [...prev, ...files])}
          onRemove={(idx) =>
            setOtherImages((prev) => prev.filter((_, i) => i !== idx))
          }
          onPreview={handlePreview}
        />
        {/* Submit button and response message */}
        <Button
          variant="contained"
          color="primary"
          type="submit"
          disabled={response === "Uploading..."}
          sx={{ mt: 2 }}
        >
          {response === "Uploading..." ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={20} color="inherit" />
              Uploading
            </Box>
          ) : (
            "Submit"
          )}
        </Button>
        {response && response !== "Uploading..." && (
          <Box sx={{ display: "flex", alignItems: "center", mt: 2, gap: 1 }}>
            <Typography
              variant="body2"
              sx={{
                color: response.includes("successfully")
                  ? "success.main"
                  : "error.main",
              }}
            >
              {response}
            </Typography>
          </Box>
        )}
      </Box>
      <ImagePreviewDialog
        open={previewOpen}
        onClose={handleClosePreview}
        imageUrl={previewImage}
      />
    </Box>
  );
}
